import { useState, useEffect } from 'react';
import { Container, Row, Col, Button, Form, InputGroup, Badge, Collapse } from 'react-bootstrap';
import usePaginatedApi from '../../hooks/usePaginatedApi';
import RecipeCard from './RecipeCard';
import LoadMore from '../common/LoadMore';
import EmptyState from '../common/EmptyState';
import LoadingState from '../common/LoadingState';
import RecipeFormModal from './form/RecipeFormModal';

export default function RecipeContainer() {
    const [showAddModal, setShowAddModal] = useState(false);

    // --- FILTER ENGINE STATE ---
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [searchInput, setSearchInput] = useState('');
    const [tagInput, setTagInput] = useState('');
    const [tags, setTags] = useState([]);
    const [ingInput, setIngInput] = useState('');
    const [ingredients, setIngredients] = useState([]);

    // The dynamic URL string that feeds the API hook
    const [endpoint, setEndpoint] = useState('/recipes');

    const { items: recipes, pagination, loading, error, loadNextPage, refresh } = usePaginatedApi(endpoint);

    // --- FILTER HANDLERS ---
    const applyFilters = () => {
        const params = new URLSearchParams();
        if (searchInput.trim()) params.append('search', searchInput.trim());
        tags.forEach(t => params.append('tags', t));
        ingredients.forEach(i => params.append('ingredients', i));

        const qs = params.toString();
        setEndpoint(qs ? `/recipes?${qs}` : '/recipes');
    };

    // Auto-apply if they delete tags or ingredients
    useEffect(() => {
        applyFilters();
        // eslint-disable-next-line
    }, [tags, ingredients]);

    const handleSearchKey = (e) => {
        if (e.key === 'Enter') applyFilters();
    };

    const handleAddTag = (e) => {
        if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            const val = tagInput.trim();
            if (val && !tags.includes(val)) setTags([...tags, val]);
            setTagInput('');
        }
    };

    const handleAddIngredient = (e) => {
        if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            const val = ingInput.trim();
            if (val && !ingredients.includes(val)) setIngredients([...ingredients, val]);
            setIngInput('');
        }
    };

    const clearFilters = () => {
        setSearchInput('');
        setTags([]);
        setIngredients([]);
        setEndpoint('/recipes');
    };

    const hasActiveFilters = searchInput.trim() !== '' || tags.length > 0 || ingredients.length > 0;

    return (
        <Container fluid className="py-4">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h3 className="fw-bold m-0">My Cookbook</h3>
                    <p className="text-muted small m-0">Plan, cook, and manage your recipes</p>
                </div>
                <Button
                    variant="primary"
                    className="d-flex align-items-center gap-2 rounded-pill px-4 fw-bold shadow-sm"
                    onClick={() => setShowAddModal(true)}
                >
                    <i className="bi bi-plus-lg"></i> New Recipe
                </Button>
            </div>

            {/* --- FILTER DASHBOARD --- */}
            <div className="bg-surface rounded-4 p-4 shadow-sm border mb-4">
                <InputGroup size="lg" className="shadow-sm mb-3 rounded-pill overflow-hidden">
                    <InputGroup.Text className="bg-white border-end-0 text-muted ps-4">
                        <i className="bi bi-search"></i>
                    </InputGroup.Text>
                    <Form.Control
                        className="border-start-0 ps-2 fw-medium border-end-0 shadow-none"
                        placeholder="Search recipes by name or author..."
                        value={searchInput}
                        onChange={e => setSearchInput(e.target.value)}
                        onKeyDown={handleSearchKey}
                    />
                    <Button variant="primary" className="fw-bold px-4 px-md-5 text-header-caps" onClick={applyFilters}>
                        Search
                    </Button>
                </InputGroup>

                <div className="d-flex justify-content-between align-items-center mb-2 px-2">
                    <Button variant="link" className="text-decoration-none p-0 text-muted fw-bold small text-header-caps" onClick={() => setShowAdvanced(!showAdvanced)}>
                        <i className={`bi bi-sliders me-1`}></i> {showAdvanced ? 'Hide Advanced Filters' : 'Advanced Filters'}
                    </Button>
                    {hasActiveFilters && (
                        <Button variant="link" className="text-decoration-none p-0 text-danger small fw-bold text-header-caps" onClick={clearFilters}>
                            <i className="bi bi-x-circle me-1"></i>Clear All
                        </Button>
                    )}
                </div>

                <Collapse in={showAdvanced}>
                    <div className="pt-3 border-top mt-2">
                        <Row className="g-4">
                            <Col md={6}>
                                <Form.Label className="small fw-bold text-muted text-header-caps mb-2"><i className="bi bi-tags me-1"></i> Filter by Tags</Form.Label>
                                <div className="p-2 border rounded-3 bg-light">
                                    <div className="d-flex flex-wrap gap-2 mb-2">
                                        {tags.map(tag => (
                                            <Badge bg="primary-subtle" text="primary" key={tag} className="rounded-pill fw-normal px-2 py-1 border border-primary-subtle">
                                                {tag} <i className="bi bi-x ms-1 cursor-pointer hover-opacity-100 opacity-50" onClick={() => setTags(tags.filter(t => t !== tag))}></i>
                                            </Badge>
                                        ))}
                                    </div>
                                    <Form.Control
                                        size="sm"
                                        className="border-0 bg-transparent shadow-none"
                                        placeholder="+ Type a tag (e.g., Dessert) and press Enter"
                                        value={tagInput}
                                        onChange={e => setTagInput(e.target.value)}
                                        onKeyDown={handleAddTag}
                                    />
                                </div>
                            </Col>
                            <Col md={6}>
                                <Form.Label className="small fw-bold text-muted text-header-caps mb-2"><i className="bi bi-basket me-1"></i> Filter by Ingredients</Form.Label>
                                <div className="p-2 border rounded-3 bg-light">
                                    <div className="d-flex flex-wrap gap-2 mb-2">
                                        {ingredients.map(ing => (
                                            <Badge bg="success-subtle" text="success" key={ing} className="rounded-pill fw-normal px-2 py-1 border border-success-subtle">
                                                {ing} <i className="bi bi-x ms-1 cursor-pointer hover-opacity-100 opacity-50" onClick={() => setIngredients(ingredients.filter(i => i !== ing))}></i>
                                            </Badge>
                                        ))}
                                    </div>
                                    <Form.Control
                                        size="sm"
                                        className="border-0 bg-transparent shadow-none"
                                        placeholder="+ Type an ingredient (e.g., Flour) and press Enter"
                                        value={ingInput}
                                        onChange={e => setIngInput(e.target.value)}
                                        onKeyDown={handleAddIngredient}
                                    />
                                </div>
                            </Col>
                        </Row>
                    </div>
                </Collapse>
            </div>

            {/* --- GRID & EMPTY STATES --- */}
            {loading && (!recipes || recipes.length === 0) ? (
                <LoadingState message="Fetching recipes..." />
            ) : error ? (
                <div className="text-danger text-center py-5 fw-bold"><i className="bi bi-exclamation-triangle fs-3 d-block mb-2"></i> Error: {error}</div>
            ) : recipes && recipes.length > 0 ? (
                <>
                    <Row className="g-4 mb-4 animation-fade-in">
                        {recipes.map(recipe => (
                            <Col xs={12} sm={6} lg={4} xl={3} key={recipe.id}>
                                <RecipeCard recipe={recipe} />
                            </Col>
                        ))}
                    </Row>
                    <LoadMore pagination={pagination} loadNextPage={loadNextPage} />
                </>
            ) : (
                <EmptyState
                    icon={hasActiveFilters ? "bi-search" : "bi-journal-plus"}
                    title={hasActiveFilters ? "No recipes matched your search" : "No recipes yet"}
                    message={hasActiveFilters ? "Try removing some tags or ingredients." : "Start building your digital cookbook by adding your first recipe."}
                    action={
                        hasActiveFilters ? (
                            <Button variant="outline-primary" className="rounded-pill px-4 mt-3" onClick={clearFilters}>Clear Filters</Button>
                        ) : (
                            <Button variant="outline-primary" className="rounded-pill px-4 mt-3" onClick={() => setShowAddModal(true)}>Add Recipe</Button>
                        )
                    }
                />
            )}

            {/* --- NEW RECIPE MODAL --- */}
            <RecipeFormModal
                show={showAddModal}
                onHide={() => setShowAddModal(false)}
                onSuccess={() => {
                    refresh();
                    setShowAddModal(false);
                }}
            />
        </Container>
    );
}
